import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { getExpoById, updateExpo } from "../Apis/ExpoAPIs";
import { getStallTypes } from "../Apis/StallTypeAPIs";

const ExhibitionLayoutLayer = () => {
    const navigate = useNavigate();

    const [expoInfo, setExpoInfo] = useState({
        name: "Global Trade Fair 2026",
        stallCount: 10,
        sqftRate: 100,
        hallSqFt: 5000
    });

    const [stalls, setStalls] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [stallTypeOptions, setStallTypeOptions] = useState([]);
    const rowsPerPage = 10;

    const { id } = useParams();

    const [generatePrefix, setGeneratePrefix] = useState("L");
    const [generateStartNo, setGenerateStartNo] = useState(1);
    const [gstRate, setGstRate] = useState(18);

    const handleBulkGenerateStalls = () => {
        if (!generatePrefix.trim()) {
            toast.error("Prefix cannot be empty for generation!");
            return;
        }
        const start = parseInt(generateStartNo);
        if (isNaN(start)) {
            toast.error("Start Number must be a valid integer!");
            return;
        }

        const reindexed = stalls.map((s, i) => ({
            ...s,
            id: `${generatePrefix.trim()}${start + i}`
        }));
        setStalls(reindexed);
        toast.success("Stall numbers generated sequentially!");
    };

    useEffect(() => {
        const fetchData = async () => {
            // 1. Fetch stall types first
            let types = [];
            const typesRes = await getStallTypes();
            if (typesRes.status) {
                types = typesRes.response?.data || typesRes.response || [];
                setStallTypeOptions(types);
            }

            const normalType = types.find(t => t.stallTypeName === "Normal");
            const normalTypeId = normalType ? (normalType._id || normalType.id) : "";

            // 2. Fetch expo details
            if (!id) return;
            const res = await getExpoById(id);
            if (res.status && res.response) {
                const data = res.response?.data || res.response;
                setExpoInfo({
                    name: data.expoName || data.title,
                    stallCount: data.numberOfStalls || 10,
                    sqftRate: data.sqmRate || 100,
                    hallSqFt: data.hallSqm || 5000
                });

                const targetCount = data.numberOfStalls || 10;

                // Load existing stalls if they exist in the backend
                if (data.stalls && data.stalls.length > 0) {
                    // Infer GST rate if possible
                    const stallWithVal = data.stalls.find(s => s.value > 0 && s.withgstvalue);
                    if (stallWithVal) {
                        const val = parseFloat(stallWithVal.value);
                        const withGst = parseFloat(stallWithVal.withgstvalue);
                        const computedRate = Math.round(((withGst - val) / val) * 100);
                        if ([5, 12, 18].includes(computedRate)) {
                            setGstRate(computedRate);
                        }
                    }

                    const loadedStalls = data.stalls.map(s => {
                        const currentType = s.type?._id || s.type;
                        // Try to find the type by ID or name in the loaded types
                        const foundType = types.find(t => (t._id || t.id) === currentType || t.stallTypeName === currentType);

                        return {
                            id: s.stallNo || "",
                            length: s.length || "",
                            width: s.width || "",
                            area: s.sqm || 0,
                            value: s.value || 0,
                            type: foundType ? (foundType._id || foundType.id) : (normalTypeId || currentType || ""),
                            status: s.status || "Pending"
                        };
                    });

                    let finalStalls = [];
                    if (loadedStalls.length >= targetCount) {
                        finalStalls = loadedStalls.slice(0, targetCount);
                    } else {
                        finalStalls = [...loadedStalls];
                        const paddingCount = targetCount - loadedStalls.length;
                        for (let i = 0; i < paddingCount; i++) {
                            const nextIndex = loadedStalls.length + i + 1;
                            finalStalls.push({
                                id: `L${nextIndex}`,
                                length: "",
                                width: "",
                                area: 0,
                                value: 0,
                                type: normalTypeId
                            });
                        }
                    }
                    setStalls(finalStalls);
                } else {
                    // Pre-generate empty rows based on stallCount
                    const initialStalls = Array.from({ length: targetCount }, (_, i) => ({
                        id: `L${i + 1}`,
                        length: "",
                        width: "",
                        area: 0,
                        value: 0,
                        type: normalTypeId
                    }));
                    setStalls(initialStalls);
                }
            }
        };

        fetchData();
    }, [id]);

    const handleUpdateStall = (index, field, value) => {
        // Prevent negative values for numeric fields
        if (['length', 'width', 'value', 'area'].includes(field) && parseFloat(value) < 0) return;

        const newStalls = [...stalls];
        const currentStall = { ...newStalls[index] };

        // Temporarily apply the change to calculate the new total
        currentStall[field] = value;

        if (field === 'length' || field === 'width') {
            const l = parseFloat(currentStall.length) || 0;
            const w = parseFloat(currentStall.width) || 0;
            const newStallArea = l * w;

            // Calculate total area if this change were applied
            const otherStallsArea = stalls.reduce((sum, s, i) => i !== index ? sum + s.area : sum, 0);
            const totalProjectedArea = otherStallsArea + newStallArea;

            // If it exceeds hall capacity, block the change
            if (totalProjectedArea > expoInfo.hallSqFt) {
                toast.error(`Hall capacity reached! You only have ${expoInfo.hallSqFt} Sqm total.`, {
                    position: "top-center",
                    autoClose: 2000
                });
                return;
            }

            currentStall.area = newStallArea;

            // Only auto-update value if type is Normal (check by ID or name)
            const isNormal = stallTypeOptions.find(t => (t._id || t.id) === currentStall.type)?.stallTypeName === "Normal" || currentStall.type === "Normal";
            if (isNormal) {
                currentStall.value = currentStall.area * expoInfo.sqftRate;
            }
        } else if (field === 'type') {
            const selectedType = stallTypeOptions.find(t => (t._id || t.id) === value);
            if (selectedType?.stallTypeName === "Normal" || (!selectedType && value === "Normal")) {
                // Recalculate if switching back to normal
                currentStall.value = currentStall.area * expoInfo.sqftRate;
            }
        } else if (field === 'area') {
            // If user manually updates area, update value if Normal
            const isNormal = stallTypeOptions.find(t => (t._id || t.id) === currentStall.type)?.stallTypeName === "Normal" || currentStall.type === "Normal";
            if (isNormal) {
                currentStall.value = parseFloat(value || 0) * expoInfo.sqftRate;
            }
        } else if (field === 'value' && value === "") {
            currentStall.value = "";
        }

        newStalls[index] = currentStall;
        setStalls(newStalls);
    };

    const handleIdChange = (index, val) => {
        const newStalls = [...stalls];
        newStalls[index].id = val;
        setStalls(newStalls);
    };

    const handleDuplicate = (index) => {
        const currentStall = stalls[index];
        if (!currentStall.length || !currentStall.width) {
            toast.error("Length and Width are mandatory to duplicate!");
            return;
        }

        const totalAreaUsed = stalls.reduce((sum, s) => sum + (parseFloat(s.area) || 0), 0);

        if (index < stalls.length - 1) {
            // Copy to existing next row
            const nextStall = stalls[index + 1];
            const projectedArea = totalAreaUsed - (parseFloat(nextStall.area) || 0) + (parseFloat(currentStall.area) || 0);

            if (projectedArea > expoInfo.hallSqFt) {
                toast.error(`Hall capacity reached! Duplicating would result in ${projectedArea.toLocaleString()} Sqm, exceeding the ${expoInfo.hallSqFt.toLocaleString()} Sqm limit.`);
                return;
            }

            const newStalls = [...stalls];
            newStalls[index + 1] = {
                ...newStalls[index + 1],
                length: currentStall.length,
                width: currentStall.width,
                area: currentStall.area,
                value: currentStall.value,
                type: currentStall.type
            };
            setStalls(newStalls);
            toast.success(`Duplicated to ${newStalls[index + 1].id}`);
        } else {
            // Add new row and copy
            if (stalls.length >= expoInfo.stallCount) {
                toast.info(`Total stall limit of ${expoInfo.stallCount} reached!`);
                return;
            }

            const projectedArea = totalAreaUsed + (parseFloat(currentStall.area) || 0);
            if (projectedArea > expoInfo.hallSqFt) {
                toast.error(`Hall capacity reached! Adding another stall would result in ${projectedArea.toLocaleString()} Sqm, exceeding the ${expoInfo.hallSqFt.toLocaleString()} Sqm limit.`);
                return;
            }

            addNewStall(true, index);
        }
    };

    const addNewStall = (isDuplicate = false, index = null) => {
        if (stalls.length >= expoInfo.stallCount) {
            toast.info(`Total stall limit of ${expoInfo.stallCount} reached!`);
            return;
        }

        const totalAreaUsed = stalls.reduce((sum, s) => sum + (parseFloat(s.area) || 0), 0);
        const prevStall = isDuplicate && index !== null ? stalls[index] : null;

        // If duplicating, check if adding this area exceeds hall capacity
        if (isDuplicate && prevStall) {
            const projectedArea = totalAreaUsed + (parseFloat(prevStall.area) || 0);
            if (projectedArea > expoInfo.hallSqFt) {
                toast.error(`Hall capacity reached! Cannot add another stall as it exceeds ${expoInfo.hallSqFt.toLocaleString()} Sqm.`);
                return;
            }
        }

        const nextId = `L${stalls.length + 1}`;

        const newStall = {
            id: nextId,
            length: prevStall ? prevStall.length : "",
            width: prevStall ? prevStall.width : "",
            area: prevStall ? prevStall.area : 0,
            value: prevStall ? prevStall.value : 0,
            type: prevStall ? prevStall.type : (stallTypeOptions[0]?._id || "Normal")
        };

        setStalls([...stalls, newStall]);
        if (isDuplicate) toast.success(`Duplicated to ${nextId}`);
        else toast.success(`Added ${nextId}`);
    };

    const handleResetStall = (index) => {
        const newStalls = [...stalls];
        newStalls[index] = {
            ...newStalls[index],
            length: "",
            width: "",
            area: 0,
            value: 0
        };
        setStalls(newStalls);
        toast.info(`Cleared data for ${newStalls[index].id}`);
    };

    const handleRemoveStall = (index) => {
        if (stalls.length === 1) {
            handleResetStall(0);
            return;
        }
        const newStalls = stalls.filter((_, i) => i !== index);
        // Re-index IDs to maintain sequential L1, L2, etc.
        const reindexed = newStalls.map((s, i) => ({
            ...s,
            id: `L${i + 1}`
        }));
        setStalls(reindexed);
        toast.error("Stall removed");
    };

    const handleSaveLayout = () => {
        // Validation 1: Check for empty stall numbers
        const emptyStall = stalls.find(s => !s.id || !s.id.toString().trim());
        if (emptyStall) {
            toast.error("Stall number cannot be empty! Please enter a value for all stalls.");
            return;
        }

        // Validation 2: Check for duplicate stall numbers
        const stallIds = stalls.map(s => s.id.toString().trim());
        const duplicates = stallIds.filter((item, index) => stallIds.indexOf(item) !== index);
        if (duplicates.length > 0) {
            const uniqueDuplicates = [...new Set(duplicates)];
            toast.error(`Duplicate Stall Numbers found: ${uniqueDuplicates.join(", ")}. Each stall must have a unique number.`);
            return;
        }

        const totalArea = stalls.reduce((sum, s) => sum + (parseFloat(s.area) || 0), 0);
        if (totalArea > expoInfo.hallSqFt) {
            toast.error(`Cannot save! Total allocated area (${totalArea} Sqm) exceeds Hall capacity (${expoInfo.hallSqFt} Sqm).`);
            return;
        }

        // const invalidStall = stalls.find(s => !s.length || parseFloat(s.length) <= 0 || !s.width || parseFloat(s.width) <= 0);
        // if (invalidStall) {
        //     toast.error(`Incomplete dimensions for stall ${invalidStall.id}. All stalls must have a positive Length and Width.`);
        //     return;
        // } 

        const payload = {
            stalls: stalls.map(s => {
                const foundType = stallTypeOptions.find(t => t.stallTypeName === s.type || (t._id || t.id) === s.type);
                const val = parseFloat(s.value) || 0;
                const withGst = val + (val * gstRate) / 100;
                return {
                    stallNo: s.id,
                    length: parseFloat(s.length) || 0,
                    width: parseFloat(s.width) || 0,
                    sqm: parseFloat(s.area) || 0,
                    value: val,
                    withgstvalue: parseFloat(withGst.toFixed(2)),
                    type: foundType ? (foundType._id || foundType.id) : s.type,
                    status: s.status || "Pending"
                };
            })
        };

        const saveLayout = async () => {
            const res = await updateExpo(id, { stalls: payload.stalls });
            if (res.status) {
                toast.success("Exhibition Layout Saved Successfully!");
                setTimeout(() => {
                    navigate(-1);
                }, 800);
            }
        };
        saveLayout();
    };

    const totalAreaUsed = stalls.reduce((sum, s) => sum + (parseFloat(s.area) || 0), 0);
    const totalValue = stalls.reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0);

    const getWithGstValue = (val) => {
        const num = parseFloat(val);
        if (isNaN(num) || num <= 0) return "0";
        const withGst = num * (1 + gstRate / 100);
        return Number(withGst.toFixed(2)).toLocaleString('en-IN', {
            minimumFractionDigits: withGst % 1 === 0 ? 0 : 2,
            maximumFractionDigits: 2
        });
    };

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden">
            <div className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between" style={{ background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)" }}>
                <h6 className="text-white mb-0 uppercase tracking-wider">Exhibition Layout Builder</h6>
                <button
                    onClick={() => navigate(-1)}
                    className="btn btn-sm d-flex align-items-center gap-1"
                    style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600" }}
                >
                    <Icon icon="solar:double-alt-arrow-left-bold" /> List Expo
                </button>
            </div>

            <div className="card-body p-24">
                <div className="row mb-24">
                    <div className="col-12">
                        <div className="bg-neutral-50 p-20 radius-8 border d-flex align-items-center justify-content-between flex-wrap gap-3">
                            <div>
                                <h6 className="mb-2 text-primary-600 fw-bold">{expoInfo.name}</h6>
                                <div className="d-flex align-items-center gap-4 text-sm fw-medium">
                                    <span className="bg-primary-600 text-white px-8 py-2 radius-4 d-flex align-items-center gap-1">
                                        <Icon icon="solar:shop-bold" /> Stalls: {expoInfo.stallCount}
                                    </span>
                                    <span>
                                        <Icon icon="solar:tag-price-bold" className="me-1 text-lg text-secondary-light" />
                                        Rate: ₹ {expoInfo.sqftRate} / Sqm
                                    </span>
                                    <span>
                                        <Icon icon="solar:ruler-pen-bold" className="me-1 text-lg text-secondary-light" />
                                        Hall: {expoInfo.hallSqFt.toLocaleString('en-IN')} Sqm
                                    </span>
                                </div>
                            </div>
                            <div className="d-flex align-items-center gap-4">
                                <div className="text-center bg-white border radius-8 px-24 py-12">
                                    <span className="d-block text-secondary-light text-xs fw-bold uppercase mb-4">Allocated Sqm</span>
                                    <span className={`fw-bold text-xl ${totalAreaUsed > expoInfo.hallSqFt ? 'text-danger-600' : 'text-success-600'}`}>
                                        {totalAreaUsed.toLocaleString('en-IN')}
                                    </span>
                                </div>
                                <div className="text-center bg-white border radius-8 px-24 py-12">
                                    <span className="d-block text-secondary-light text-xs fw-bold uppercase mb-4">Total Value</span>
                                    <span className="fw-bold text-xl text-primary-600">
                                        ₹ {totalValue.toLocaleString()}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row mb-24">
                    <div className="col-12">
                        <div className="bg-white p-20 radius-8 border">
                            <h6 className="mb-16 text-md fw-bold d-flex align-items-center gap-2" style={{ color: "#ed1c24" }}>
                                <Icon icon="solar:magic-stick-3-bold" className="text-xl" /> Stall Number Sequence Generator
                            </h6>
                            <div className="d-flex align-items-center flex-wrap gap-4">
                                <div className="d-flex align-items-center gap-2">
                                    <label className="text-sm fw-semibold text-secondary-light mb-0" style={{ whiteSpace: "nowrap" }}>Prefix:</label>
                                    <input
                                        type="text"
                                        className="form-control radius-4 fw-medium text-center"
                                        style={{ width: "80px", height: "38px" }}
                                        value={generatePrefix}
                                        onChange={(e) => setGeneratePrefix(e.target.value)}
                                        placeholder="e.g. L"
                                    />
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <label className="text-sm fw-semibold text-secondary-light mb-0" style={{ whiteSpace: "nowrap" }}>Start Number:</label>
                                    <input
                                        type="number"
                                        className="form-control radius-4 fw-medium text-center"
                                        style={{ width: "100px", height: "38px" }}
                                        value={generateStartNo}
                                        min="0"
                                        onChange={(e) => setGenerateStartNo(parseInt(e.target.value) || 0)}
                                    />
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <label className="text-sm fw-semibold text-secondary-light mb-0" style={{ whiteSpace: "nowrap" }}>GST:</label>
                                    <select
                                        className="form-select radius-4 fw-bold text-center"
                                        style={{ width: "90px", height: "38px" }}
                                        value={gstRate}
                                        onChange={(e) => setGstRate(parseInt(e.target.value) || 18)}
                                    >
                                        <option value="5">5%</option>
                                        <option value="12">12%</option>
                                        <option value="18">18%</option>
                                    </select>
                                </div>
                                <button
                                    onClick={handleBulkGenerateStalls}
                                    className="btn btn-sm radius-4 fw-bold px-20 py-10 transition-all d-flex align-items-center gap-2 ms-auto"
                                    style={{ backgroundColor: "#ed1c24", color: "white", height: "38px" }}
                                >
                                    <Icon icon="solar:play-circle-bold" className="text-lg" /> Generate Sequence
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="border radius-8" style={{ overflowX: 'hidden' }}>
                    <table className="table bordered-table sticky-header mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr style={{ backgroundColor: "#ed1c24" }}>
                                <th scope="col" style={{ color: "white", fontWeight: "bold", width: "8%" }}>Stall No.</th>
                                <th scope="col" style={{ color: "white", fontWeight: "bold", width: "9%" }}>Length <span className="text-white">*</span></th>
                                <th scope="col" className="text-center" style={{ color: "white", fontWeight: "bold", width: "3%" }}></th>
                                <th scope="col" style={{ color: "white", fontWeight: "bold", width: "9%" }}>Width <span className="text-white">*</span></th>
                                <th scope="col" className="text-center" style={{ color: "white", fontWeight: "bold", width: "3%" }}></th>
                                <th scope="col" className="text-center" style={{ color: "white", fontWeight: "bold", width: "7%" }}>Sqm</th>
                                <th scope="col" className="text-center" style={{ color: "white", fontWeight: "bold", width: "15%" }}>Value (₹)</th>
                                <th scope="col" className="text-center" style={{ color: "white", fontWeight: "bold", width: "15%" }}>With GST (₹)</th>
                                <th scope="col" className="text-center" style={{ color: "white", fontWeight: "bold", width: "12%" }}>Type</th>
                                <th scope="col" className="text-center" style={{ color: "white", fontWeight: "bold", width: "19%" }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {stalls
                                .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                                .map((stall, pIndex) => {
                                    const index = (currentPage - 1) * rowsPerPage + pIndex;
                                    const isLastRow = index === stalls.length - 1;
                                    const isLimitReached = stalls.length >= expoInfo.stallCount;

                                    return (
                                        <tr key={index}>
                                            <td className="align-middle">
                                                <input
                                                    type="text"
                                                    className="form-control radius-4 fw-bold text-primary-600"
                                                    style={{ height: "40px", fontSize: "16px" }}
                                                    value={stall.id}
                                                    onChange={(e) => handleIdChange(index, e.target.value)}
                                                />
                                            </td>
                                            <td className="align-middle">
                                                <input
                                                    type="number"
                                                    className={`form-control radius-4 text-center px-1 ${(!stall.length || parseFloat(stall.length) <= 0) ? "is-invalid border-danger" : ""}`}
                                                    style={{ height: "40px", fontSize: "16px", fontWeight: "600" }}
                                                    value={stall.length}
                                                    placeholder="L"
                                                    min="0"
                                                    onKeyDown={(e) => ["-", "e", "E"].includes(e.key) && e.preventDefault()}
                                                    onChange={(e) => handleUpdateStall(index, 'length', e.target.value)}
                                                />
                                            </td>
                                            <td className="text-center fw-bold text-xl text-secondary-light align-middle">X</td>
                                            <td className="align-middle">
                                                <input
                                                    type="number"
                                                    className={`form-control radius-4 text-center px-1 ${(!stall.width || parseFloat(stall.width) <= 0) ? "is-invalid border-danger" : ""}`}
                                                    style={{ height: "40px", fontSize: "16px", fontWeight: "600" }}
                                                    value={stall.width}
                                                    placeholder="W"
                                                    min="0"
                                                    onKeyDown={(e) => ["-", "e", "E"].includes(e.key) && e.preventDefault()}
                                                    onChange={(e) => handleUpdateStall(index, 'width', e.target.value)}
                                                />
                                            </td>
                                            <td className="text-center fw-bold text-xl text-secondary-light align-middle">=</td>
                                            <td className="align-middle px-12">
                                                <div className="d-flex align-items-center justify-content-center border radius-4 bg-white fw-bold text-primary-600 text-lg h-40-px px-2">
                                                    <span>{stall.area.toLocaleString('en-IN')}</span>
                                                </div>
                                            </td>
                                            <td className="align-middle px-12">
                                                <div className="d-flex align-items-center justify-content-between px-2 border radius-4 bg-white fw-bold h-40-px" style={{ fontSize: "16px", color: "#ed1c24" }}>
                                                    <span>₹</span>
                                                    <input
                                                        type="text"
                                                        className="form-control border-0 p-0 fw-bold text-end pe-1"
                                                        style={{ height: "30px", fontSize: "16px", color: "#ed1c24" }}
                                                        value={stall.value ? Number(stall.value).toLocaleString('en-IN') : ""}
                                                        placeholder="0"
                                                        onChange={(e) => {
                                                            const val = e.target.value.replace(/,/g, '');
                                                            if (!isNaN(val)) handleUpdateStall(index, 'value', val);
                                                        }}
                                                    />
                                                </div>
                                            </td>
                                            <td className="align-middle px-12">
                                                <div className="d-flex align-items-center justify-content-between px-2 border radius-4 bg-light fw-bold h-40-px" style={{ fontSize: "16px", color: "#28a745" }}>
                                                    <span>₹</span>
                                                    <span className="pe-1 text-end w-100">
                                                        {getWithGstValue(stall.value)}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="align-middle">
                                                <select
                                                    className="form-select radius-4 fw-bold"
                                                    style={{ height: "40px", fontSize: "14px" }}
                                                    value={stall.type}
                                                    onChange={(e) => handleUpdateStall(index, 'type', e.target.value)}
                                                >
                                                    <option value="">Select Type</option>
                                                    {stallTypeOptions.map(t => (
                                                        <option key={t._id || t.id} value={t._id || t.id}>{t.stallTypeName}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="text-center align-middle">
                                                <div className="d-flex gap-2 justify-content-center">
                                                    {stall.length && stall.width && (!stalls[index + 1] || (!stalls[index + 1].length && !stalls[index + 1].width)) && !(isLastRow && isLimitReached) ? (
                                                        <button
                                                            className="btn btn-primary-600 btn-sm radius-4 d-flex align-items-center gap-1"
                                                            onClick={() => handleDuplicate(index)}
                                                            title="Duplicate to next row"
                                                            style={{ backgroundColor: "#ed1c24", borderColor: "#ed1c24" }}
                                                        >
                                                            <Icon icon="solar:copy-bold" className="text-lg" /> Duplicate
                                                        </button>
                                                    ) : (
                                                        (stalls.length > 1 || isLastRow) && (
                                                            <button
                                                                className="btn btn-outline-warning btn-sm radius-4 fw-bold px-12"
                                                                onClick={() => handleResetStall(index)}
                                                                title="Clear row data"
                                                                style={{ color: "#ff8000", borderColor: "#ff8000" }}
                                                            >
                                                                Clear
                                                            </button>
                                                        )
                                                    )}

                                                    {isLastRow && isLimitReached && (
                                                        <span className="text-danger fw-bold text-xs uppercase px-8 align-self-center">Limit Reached</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>

                <div className="card-footer bg-transparent border-top py-16 px-24 d-flex align-items-center justify-content-between">
                    <div className="text-secondary-light text-sm">
                        Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, stalls.length)} of {stalls.length} stalls
                    </div>
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-sm radius-8 px-16 fw-bold transition-all"
                            disabled={currentPage === 1}
                            onClick={() => {
                                setCurrentPage(prev => prev - 1);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            style={{
                                backgroundColor: currentPage === 1 ? "#f8f9fa" : "#ed1c24",
                                color: currentPage === 1 ? "#adb5bd" : "white",
                                border: "1px solid " + (currentPage === 1 ? "#dee2e6" : "#ed1c24")
                            }}
                        >
                            Previous
                        </button>
                        <button
                            className="btn btn-sm radius-8 px-16 fw-bold transition-all"
                            disabled={currentPage >= Math.ceil(stalls.length / rowsPerPage)}
                            onClick={() => {
                                setCurrentPage(prev => prev + 1);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            style={{
                                backgroundColor: currentPage >= Math.ceil(stalls.length / rowsPerPage) ? "#f8f9fa" : "#ed1c24",
                                color: currentPage >= Math.ceil(stalls.length / rowsPerPage) ? "#adb5bd" : "white",
                                border: "1px solid " + (currentPage >= Math.ceil(stalls.length / rowsPerPage) ? "#dee2e6" : "#ed1c24")
                            }}
                        >
                            Next
                        </button>
                    </div>
                </div>

                <div className="mt-24 d-flex justify-content-end px-24 pb-24">
                    <button
                        className="btn radius-8 px-40 py-12"
                        style={{ backgroundColor: "#ed1c24", color: "white", fontWeight: "700" }}
                        onClick={handleSaveLayout}
                    >
                        Save Layout
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExhibitionLayoutLayer;
